/** @jsx jsx */
import { css, jsx } from '@compiled/react';

import { WidthObserver } from '@atlaskit/width-detector';

import { fg } from '@confluence/feature-gating';
import { getIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/getIsNav4Enabled.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

const baseStyles = css({
	position: 'relative',
	overflow: 'visible',
});

// This z-index value is higher than the sticky table header
const zIndex11Styles = css({
	zIndex: '11',
});

// This z-index value is higher than the content header
const zIndex13Styles = css({
	zIndex: '13',
});

const popupPortalContainerId = 'highlight-actions-portal-container';
export { popupPortalContainerId };

let popupPortalContainerWidth;

function onResize(width) {
	// Hide popup when the container is resized.
	if (width !== popupPortalContainerWidth) {
		popupPortalContainerWidth = width;
		window.dispatchEvent(new CustomEvent('highlight-actions.container.resized'));
	}
}

export const HighlightActionsProvider = () => {
	return (
		<div
			css={[
				baseStyles,
				fg('confluence_highlight_actions_zindex_fix') ? zIndex11Styles : zIndex13Styles,
			]}
			id={popupPortalContainerId}
		>
			<WidthObserver setWidth={onResize} />
		</div>
	);
};

/**
 *  In Nav4, we need to render the HighlightActionsProvider inside the #AkMainContent container instead of at the top of
 *  our app in <App />. This is because Nav4 uses a grid. So in Nav4 the window itself no longer scrolls; the #AkMainContent
 *  element does.
 *
 *  These HighlightActionsProviderForNav3 and HighlightActionsProviderForNav4 components allow us to render
 *  HighlightActions in different places depending on which nav is enabled.
 */

// Only renders the HighlightActionsProvider if we're using Nav3
export const HighlightActionsProviderForNav3 = () => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// render null for first render during hydration to prevent mismatch
	if (renderServerPlaceholder) {
		return null;
	}
	return getIsNav4Enabled() ? null : <HighlightActionsProvider />;
};

// Only renders the Highlight Actions Provider if we're using Nav4
export const HighlightActionsProviderForNav4 = () => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// render null for first render during hydration to prevent mismatch
	if (renderServerPlaceholder) {
		return null;
	}
	return getIsNav4Enabled() ? <HighlightActionsProvider /> : null;
};
